import {
  Pressable,
  Text,
  ActivityIndicator,
  StyleSheet,
  type AccessibilityRole, type AccessibilityState, type StyleProp, type TextStyle,
  type ViewStyle,
} from 'react-native';
import { colors, controlSize as controlSizes, motion } from '@tabbio/tokens';
import { useTheme } from '../../hooks/useTheme';
import { useFont } from '../../providers/FontProvider';
import { useState } from 'react';

type ButtonVariant = 'solid' | 'soft' | 'outline' | 'ghost';
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type ButtonColor = 'primary' | 'secondary' | 'danger' | 'success' | 'contrast';

interface NativeButtonProps {
  title?: string;
  onPress: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  color?: ButtonColor;
  disabled?: boolean;
  loading?: boolean;
  pill?: boolean;
  block?: boolean;
  icon?: React.ReactNode;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  haptic?: 'light' | 'medium' | 'selection' | 'none';
  style?: ViewStyle;
  textStyle?: StyleProp<TextStyle>;
  textColor?: string;
  accessibilityRole?: AccessibilityRole;
  accessibilityState?: AccessibilityState;
  accessibilityLabel?: string;
  accessibilityHint?: string;
}

const COLOR_MAP = {
  primary: { solid: colors.primary, text: '#ffffff' },
  secondary: { solid: colors.secondary, text: '#ffffff' },
  danger: { solid: colors.danger, text: '#ffffff' },
  success: { solid: colors.success, text: '#ffffff' },
  // `contrast` bg/text resolve from the theme (see getVariantStyles); these are fallbacks.
  contrast: { solid: '#000000', text: '#ffffff' },
};

export function NativeButton({
  title,
  onPress,
  variant = 'solid',
  size = 'md',
  color = 'primary',
  disabled = false,
  loading = false,
  pill = false,
  block = false,
  icon,
  iconLeft,
  iconRight,
  style,
  textStyle,
  textColor,
  accessibilityRole = 'button',
  accessibilityState,
  accessibilityLabel,
  accessibilityHint,
}: NativeButtonProps) {
  const { theme } = useTheme();
  const { fonts } = useFont();
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);

  const sizeTokens = controlSizes[size];
  const colorTokens = COLOR_MAP[color];
  const isIconOnly = icon && !title;
  const isDisabled = disabled || loading;

  const getVariantStyles = () => {
    switch (variant) {
      case 'solid':
        if (color === 'contrast') {
          return {
            bg: theme.buttonContrastBg,
            bgHover: theme.buttonContrastBg,
            textColor: theme.buttonContrastText,
          };
        }
        return {
          bg: colorTokens.solid,
          bgHover: colorTokens.solid,
          textColor: colorTokens.text,
        };
      case 'soft':
        return {
          bg: theme.buttonSoftBg,
          bgHover: theme.buttonSoftBg,
          textColor: theme.buttonSoftText,
        };
      case 'outline':
        return {
          bg: 'transparent',
          bgHover: theme.buttonGhostHover,
          textColor: theme.text,
          borderColor: theme.buttonOutlineBorder,
          borderWidth: 1,
        };
      case 'ghost':
        return {
          bg: 'transparent',
          bgHover: theme.buttonGhostHover,
          textColor: theme.text,
        };
    }
  };

  const v = getVariantStyles();

  return (
    <Pressable
      accessibilityLabel={accessibilityLabel ?? title}
      accessibilityHint={accessibilityHint}
      {...((accessibilityRole === 'checkbox' || accessibilityRole === 'radio') && !isDisabled ? {
        onKeyDown: (event: { key: string; preventDefault: () => void }) => {
          // RN web only handles Space for button/link roles.
          if (event.key === ' ' || event.key === 'Spacebar') { event.preventDefault(); onPress(); }
        },
      } : {})}
      onPress={onPress}
      disabled={isDisabled}
      onPressIn={() => setPressed(true)}
      onPressOut={() => setPressed(false)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[
        styles.base,
        {
          height: sizeTokens.height,
          paddingHorizontal: isIconOnly ? 0 : sizeTokens.paddingHorizontal,
          borderRadius: pill ? 9999 : sizeTokens.radius,
          backgroundColor: hovered && !isDisabled ? v.bgHover : v.bg,
          borderColor: v.borderColor,
          borderWidth: v.borderWidth,
          alignSelf: block ? 'stretch' : undefined,
          // CSS transitions (react-native-web supports these)
          transitionProperty:
            'transform, background-color, box-shadow, border-color',
          transitionDuration: `${motion.duration.fast}ms`,
          transitionTimingFunction: motion.easing.enter,
          transform: [{ scale: pressed && !isDisabled ? motion.press.scale : 1 }],
          // Focus ring
          outlineColor: theme.focusRing,
          outlineOffset: 2,
          outlineWidth: 0,
          cursor: isDisabled ? 'default' : 'pointer',
          opacity: isDisabled ? 0.35 : 1,
        } as any,
        style,
      ]}
      focusable={!isDisabled}
      accessibilityRole={accessibilityRole}
      accessibilityState={{ ...accessibilityState, disabled: isDisabled, busy: loading }}
      aria-checked={accessibilityRole === 'checkbox' ? accessibilityState?.checked : accessibilityRole === 'radio' ? accessibilityState?.selected : undefined}
      aria-disabled={isDisabled}
      aria-busy={loading}
    >
      {loading ? (
        <ActivityIndicator size="small" color={v.textColor} />
      ) : (
        <>
          {iconLeft}
          {icon && !title ? icon : null}
          {title ? (
            <Text
              style={[
                styles.text,
                {
                  fontSize: sizeTokens.fontSize,
                  color: textColor ?? v.textColor,
                  fontFamily: fonts.medium,
                },
                textStyle,
              ]}
            >
              {title}
            </Text>
          ) : null}
          {iconRight}
        </>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  base: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  text: {
    fontWeight: '600',
  },
});
